/**
 * Cómo se identifica una solicitud en los listados de estatus de servicio.
 *
 * Producto y Marca no bastan cuando hay varios modelos del mismo producto, así
 * que el renglón muestra también Modelo y el Número de Referencia de Finanzas.
 * Van como línea secundaria dentro de las celdas que ya existen —no como
 * columnas nuevas— porque la tabla ya se desplaza horizontalmente y ensancharla
 * más empeoraría justo lo que se quiere arreglar.
 *
 * Vive en un solo componente para que Telecom y Seguridad presenten lo mismo:
 * hoy cada sector tiene su propia tabla y ya habían divergido.
 */

/** Guion para el dato ausente. Nunca "null" ni una celda en blanco. */
const VACIO = "—";

/**
 * Folio SICEN con la pre-solicitud (Telecom) y el Número de Referencia debajo.
 *
 * Son identificadores **independientes**: el folio lo genera SICEN al crear la
 * solicitud y la referencia (UPT) la captura Finanzas al validar el pago. Uno
 * no sustituye ni se deriva del otro, por eso se ven los dos y la referencia
 * dice "—" mientras el trámite no llegue a esa etapa.
 */
export function CeldaFolio({
  folio,
  numReferencia,
  preSolicitud,
}: {
  folio: string;
  numReferencia?: string | null;
  /**
   * Número de pre-solicitud NT-AAMMDD-NN. Solo existe en Telecom (NT es su
   * prefijo); Seguridad no lo pasa y la línea no se pinta.
   */
  preSolicitud?: string | null;
}) {
  const ref = numReferencia?.trim();
  const pre = preSolicitud?.trim();
  return (
    <div className="leading-tight">
      <div className="font-mono text-xs font-medium">{folio}</div>
      {pre && (
        <div className="mt-0.5 font-mono text-[11px] text-fg-subtle" title="Número de pre-solicitud asignado por SICEN">
          {pre}
        </div>
      )}
      <div className="mt-0.5 font-mono text-[11px] text-fg-faint" title="Número de Referencia capturado por Finanzas">
        Ref. {ref ? <span className="text-fg-subtle">{ref}</span> : VACIO}
      </div>
    </div>
  );
}

/** Cuántos modelos se ven antes de resumir el resto en «+N». */
const MODELOS_VISIBLES = 2;

/**
 * Producto con marca y modelos debajo.
 *
 * Los modelos vienen como texto separado por comas y pueden ser muchos —hay
 * solicitudes con cinco—, así que se muestran los primeros y el resto se
 * resume; la lista completa queda en el tooltip para no romper el renglón.
 */
export function CeldaProducto({
  producto,
  marca,
  modelos,
}: {
  producto?: string | null;
  marca?: string | null;
  modelos?: string | null;
}) {
  const lista = (modelos ?? "")
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean);
  const visibles = lista.slice(0, MODELOS_VISIBLES);
  const ocultos = lista.length - visibles.length;

  return (
    <div className="leading-tight">
      <div>{producto?.trim() || VACIO}</div>
      <div className="mt-0.5 text-[11px] text-fg-faint">
        {marca?.trim() || VACIO}
        {" · "}
        {lista.length === 0 ? (
          VACIO
        ) : (
          <span className="font-mono text-fg-subtle" title={lista.join(", ")}>
            {visibles.join(", ")}
            {ocultos > 0 && <span className="text-fg-faint"> +{ocultos}</span>}
          </span>
        )}
      </div>
    </div>
  );
}
